// Add Medication modal — posts to /patients/:patientId/medications.
// Fields per DRS P3: generic_name, drug_name, dosage_mg+unit, route,
//                    frequency, start/end dates, prescribed_by, is_critical.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";

interface Props { patientId: string; onClose: () => void; onCreated: () => void; }

interface MedPayload {
  generic_name: string; drug_name: string; brand_name?: string; drug_class?: string;
  dosage_mg: number; dosage_unit: string; route: string; frequency: string;
  scheduled_times: string[]; start_date: string; end_date?: string;
  refill_date?: string; prescribed_by: string; indication?: string;
  is_critical: boolean; notes?: string;
}

const FREQ_OPTIONS = [
  { value: "",                 label: "Select frequency…" },
  { value: "once_daily",       label: "Once daily" },
  { value: "twice_daily",      label: "Twice daily" },
  { value: "three_times_daily",label: "Three times daily" },
  { value: "four_times_daily", label: "Four times daily" },
  { value: "every_other_day",  label: "Every other day" },
  { value: "weekly",           label: "Weekly" },
  { value: "as_needed",        label: "As needed" },
  { value: "other",            label: "Other" },
];

const ROUTE_OPTIONS = [
  { value: "",                       label: "Select route…" },
  { value: "oral",                   label: "Oral" },
  { value: "sublingual",             label: "Sublingual" },
  { value: "topical",                label: "Topical" },
  { value: "inhaled",                label: "Inhaled" },
  { value: "injected_subcutaneous",  label: "Injected (subcutaneous)" },
  { value: "injected_im",            label: "Injected (intramuscular)" },
  { value: "eye_drops",              label: "Eye drops" },
  { value: "ear_drops",              label: "Ear drops" },
  { value: "rectal",                 label: "Rectal" },
  { value: "patch",                  label: "Patch" },
];

const DOSAGE_UNIT_OPTIONS = [
  { value: "mg",    label: "mg" },   { value: "mcg",   label: "mcg" },
  { value: "units", label: "units" },{ value: "ml",    label: "ml" },
  { value: "IU",    label: "IU" },   { value: "g",     label: "g" },
];

const PRESCRIBED_BY_OPTIONS = [
  { value: "",                    label: "Select prescriber…" },
  { value: "gp",                  label: "GP" },
  { value: "specialist",          label: "Specialist" },
  { value: "emergency_dept",      label: "Emergency dept" },
  { value: "tele_consult_nsofar", label: "Tele-consult (Nsofar)" },
  { value: "self_prescribed",     label: "Self prescribed" },
  { value: "unknown",             label: "Unknown" },
];

const INPUT_CLS = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500";
const INPUT_ERR_CLS = "w-full rounded-lg border border-red-400 px-3 py-2 text-sm bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400";

function parseScheduledTimes(raw: string): string[] {
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function CriticalToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }): JSX.Element {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
      <div className="relative mt-0.5 shrink-0">
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <div className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-red-500" : "bg-slate-300"}`} />
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
      </div>
      <div>
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          {checked && <span className="text-red-500 text-base leading-none" aria-hidden>⚠</span>}
          Critical medication
        </span>
        <p className="text-xs text-slate-400 mt-0.5">Mark as critical for anticoagulants, insulin, antiepileptics, and cardiac medications. Non-adherence will trigger an immediate CS alert.</p>
      </div>
    </label>
  );
}

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function AddMedicationModal({ patientId, onClose, onCreated }: Props): JSX.Element {
  const [genericName,   setGenericName]   = useState("");
  const [drugName,      setDrugName]      = useState("");
  const [brandName,     setBrandName]     = useState("");
  const [drugClass,     setDrugClass]     = useState("");
  const [dosageMg,      setDosageMg]      = useState("");
  const [dosageUnit,    setDosageUnit]    = useState("mg");
  const [routeVal,      setRouteVal]      = useState("");
  const [frequency,     setFrequency]     = useState("");
  const [scheduledTimes,setScheduledTimes]= useState("");
  const [startDate,     setStartDate]     = useState("");
  const [endDate,       setEndDate]       = useState("");
  const [refillDate,    setRefillDate]    = useState("");
  const [prescribedBy,  setPrescribedBy]  = useState("");
  const [indication,    setIndication]    = useState("");
  const [isCritical,    setIsCritical]    = useState(false);
  const [notes,         setNotes]         = useState("");
  const [errors,        setErrors]        = useState<Partial<Record<string, string>>>({});

  const mutation = useMutation({
    mutationFn: (payload: MedPayload) =>
      apiFetch(`/patients/${patientId}/medications`, { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => onCreated(),
  });

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
  function validate(): boolean {
    const e: Partial<Record<string, string>> = {};
    if (!genericName.trim())              e.generic_name  = "Generic name is required.";
    if (!drugName.trim())                 e.drug_name     = "Drug name is required.";
    if (!dosageMg || Number(dosageMg) <= 0) e.dosage_mg   = "Dose must be greater than 0.";
    if (!routeVal)                        e.route         = "Route is required.";
    if (!frequency)                       e.frequency     = "Frequency is required.";
    if (!startDate)                       e.start_date    = "Start date is required.";
    if (!prescribedBy)                    e.prescribed_by = "Prescriber is required.";
    if (endDate && startDate && endDate < startDate)
      e.end_date = "End date must be on or after start date.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent): void {
    ev.preventDefault();
    if (!validate()) return;
    const payload: MedPayload = {
      generic_name: genericName.trim(), drug_name: drugName.trim(),
      dosage_mg: Number(dosageMg), dosage_unit: dosageUnit,
      route: routeVal, frequency,
      scheduled_times: parseScheduledTimes(scheduledTimes),
      start_date: startDate, prescribed_by: prescribedBy, is_critical: isCritical,
    };
    if (brandName.trim())   payload.brand_name  = brandName.trim();
    if (drugClass.trim())   payload.drug_class  = drugClass.trim();
    if (endDate)            payload.end_date    = endDate;
    if (refillDate)         payload.refill_date = refillDate;
    if (indication.trim())  payload.indication  = indication.trim();
    if (notes.trim())       payload.notes       = notes.trim();
    mutation.mutate(payload);
  }

  return (
    <Modal title="Add Medication" onClose={onClose}>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <FormField label="Generic name (WHO INN)" value={genericName} onChange={setGenericName}
            required placeholder="e.g. amlodipine" error={errors.generic_name} />
          <p className="text-xs text-slate-400 mt-1">Use the generic international name</p>
        </div>
        <FormField label="Drug Name" value={drugName} onChange={setDrugName} required error={errors.drug_name} />
        <FormField label="Brand Name" value={brandName} onChange={setBrandName} placeholder="e.g. Crocin, Metformin XR" />
        <FormField label="Drug Class" value={drugClass} onChange={setDrugClass} placeholder="e.g. ACE inhibitor" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Dose<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input type="number" min={0} step={0.5} value={dosageMg}
              onChange={(e) => setDosageMg(e.target.value)}
              className={errors.dosage_mg ? INPUT_ERR_CLS : INPUT_CLS} />
            {errors.dosage_mg && <p className="mt-1 text-xs text-red-600">{errors.dosage_mg}</p>}
          </div>
          <SelectField label="Unit" value={dosageUnit} onChange={setDosageUnit}
            options={DOSAGE_UNIT_OPTIONS} required />
        </div>
        <SelectField label="Route of administration" value={routeVal} onChange={setRouteVal}
          options={ROUTE_OPTIONS} required error={errors.route} />
        <SelectField label="Frequency" value={frequency} onChange={setFrequency}
          options={FREQ_OPTIONS} required error={errors.frequency} />
        <FormField label="Scheduled Times" value={scheduledTimes} onChange={setScheduledTimes}
          placeholder="e.g. 08:00, 20:00 (comma-separated)" />
        <FormField label="Start Date" type="date" value={startDate} onChange={setStartDate}
          required error={errors.start_date} />
        <FormField label="End date (if known)" type="date" value={endDate} onChange={setEndDate}
          error={errors.end_date} />
        <FormField label="Refill Date" type="date" value={refillDate} onChange={setRefillDate} />
        <SelectField label="Prescribed by" value={prescribedBy} onChange={setPrescribedBy}
          options={PRESCRIBED_BY_OPTIONS} required error={errors.prescribed_by} />
        <FormField label="Indication (reason prescribed)" value={indication} onChange={setIndication}
          placeholder="e.g. hypertension management" />
        <CriticalToggle checked={isCritical} onChange={setIsCritical} />
        <FormField label="Notes" value={notes} onChange={setNotes} placeholder="Optional notes" />

        {mutation.isError && (
          <p role="alert" className="text-xs text-red-600">Failed to add medication. Please try again.</p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">Cancel</button>
          <button type="submit" disabled={mutation.isPending}
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors disabled:opacity-50">
            {mutation.isPending ? "Saving…" : "Add Medication"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
